import { TFunction, ITyreInfoBox } from '@wyz/types';
import {
  DEFAULT_TIRE_BRAND_LOGO,
  DEFAULT_TIRE_BRAND_WIDTH,
  getBrandLogoUrl,
  getHomologationBrandImage,
} from '@wyz/utils';
import * as React from 'react';
import { Badge, Flex, Icon, Img, SoftBadge, Text, Tooltip } from '../../index';
import { BrandLoader, HomologationLogo, SeasonIcon } from './index';
import { PropsWithChildren } from 'react';

const VEHICLE_TYPE_TRANSLATION: Record<string, string> = {
  'CAM': 'vehicle_type.cam',
  '4X4': 'vehicle_type.4x4',
  'TO': 'vehicle_type.to',
};

type Props = PropsWithChildren<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any>;
  tyre: ITyreInfoBox;
  imageBaseUrl: string;
  direction?: 'vertical' | 'horizontal';
  mode?: 'FULL' | 'PARTIAL';
  context?: 'quote';
  disabled?: boolean;
  showHomologation?: boolean;
  showEan?: boolean;
}>;
const TyreInfoBox = ({
  t,
  tyre,
  direction = 'vertical',
  mode = 'FULL',
  imageBaseUrl,
  context,
  children,
  disabled = false,
  showHomologation = true,
  showEan = true,
}: Props) => {
  const brandLogo = React.useMemo(
    () => getBrandLogoUrl(tyre.brand),
    [tyre.brand],
  );
  const isQuote = context === 'quote';

  const homologations = tyre.homologations ?? [];
  const homologationBrands = homologations
    .map((homologation) => homologation.brand)
    .join(', ');
  const homologationImageBrand = getHomologationBrandImage(homologations);
  const homologationHover = homologationImageBrand ? (
    <HomologationLogo
      brand={homologationImageBrand}
      imageBaseUrl={imageBaseUrl}
      fallback={tyre.prodInfo}
    />
  ) : (
    tyre.prodInfo
  );

  return (
    <div>
      <Flex
        direction={direction == 'horizontal' ? 'row' : 'column'}
        className={direction === 'horizontal' ? 'space-x-4' : ''}
      >
        {disabled === false ? (
          <Flex className='space-x-2'>
            <Tooltip label={tyre.brand}>
              <Img
                src={brandLogo}
                baseUrl={imageBaseUrl}
                data-cy={`product-info-brand-${tyre.ean}`}
                defaultImage={DEFAULT_TIRE_BRAND_LOGO}
                alt={tyre.brand}
                width={DEFAULT_TIRE_BRAND_WIDTH.sm}
                loader={BrandLoader}
                style={{ objectFit: 'scale-down', maxHeight: 40 }}
              />
            </Tooltip>
            {tyre.segment && <Badge bg='chip'>{tyre.segment}</Badge>}
          </Flex>
        ) : (
          <div style={{ width: DEFAULT_TIRE_BRAND_WIDTH.sm, height: 40 }}>
            {' '}
          </div>
        )}
        <div className='space-y-2'>
          <Flex alignItems='center' className='gap-2'>
            <Text
              weight='bold'
              heading='sm'
              color='tertiary'
              id={`product-info-profile-${tyre.ean}`}
            >
              {tyre.profile}
            </Text>
            {isQuote && tyre.dimension && (
              <Badge
                pill
                bg='info'
                className='fs-xxs fw-bold'
                id={`product-info-dimension-${tyre.ean}`}
              >
                {tyre.dimension}
              </Badge>
            )}
          </Flex>
          {((!isQuote && tyre.dimension) ||
            tyre.season ||
            tyre.vehicleType) && (
            <div>
              <Flex className='space-x-2' alignItems='center'>
                {!isQuote && tyre.dimension && (
                  <Badge
                    pill
                    bg='info'
                    className='fs-xxs fw-semi-bold'
                    id={`product-info-dimension-${tyre.ean}`}
                  >
                    {tyre.dimension}
                  </Badge>
                )}
                {tyre.season && <SeasonIcon value={tyre.season} size='xs' />}
                {tyre.seasonPmsf === 'Y' && (
                  <Icon icon='mountain-flake' size='xs' />
                )}

                {tyre.vehicleType && (
                  <SoftBadge
                    pill
                    bg='classic'
                    id={`product-info-vehicle-type-${tyre.ean}`}
                  >
                    {t(
                      VEHICLE_TYPE_TRANSLATION[tyre.vehicleType] ??
                        tyre.vehicleType,
                    )}
                  </SoftBadge>
                )}
                {children}
                {showHomologation && homologations.length !== 0 && (
                  <Tooltip
                    label={homologationHover}
                    className='homologation-tooltip'
                  >
                    <SoftBadge
                      pill
                      bg='classic'
                      className='fs-xs fw-semi-bold'
                      id={`product-info-homologation-${tyre.ean}`}
                    >
                      {homologationBrands}
                    </SoftBadge>
                  </Tooltip>
                )}
              </Flex>
            </div>
          )}
          {mode === 'FULL' && (
            <Flex className='gap-1' direction='column'>
              {disabled && (
                <Text heading='xxs' weight='semi-bold' color='secondary-gray'>
                  {t('common.tyre.no_tyre_found')}
                </Text>
              )}
              {disabled === false && (
                <>
                  {showEan && (
                    <Text
                      heading='xxs'
                      weight='semi-bold'
                      color='secondary-gray'
                      id={`product-info-ean-${tyre.ean}`}
                    >
                      {/* Note these translations keys are used in wtb (common ns) and wyzcore (common ns) */}

                      {t('app.common.product.ean_value', {
                        value: tyre.ean,
                      })}
                    </Text>
                  )}
                  {/** since reference here is constReference in Order, we cannot know if its alternative or constructor  */}
                  {'alternativeReference' in tyre &&
                    tyre.alternativeReference && (
                      <Text
                        heading='xxs'
                        weight='semi-bold'
                        color='secondary-gray'
                        id={`product-info-alternative-reference-${tyre.ean}`}
                      >
                        {t('app.common.product.reference_value', {
                          value: tyre.alternativeReference,
                        })}
                      </Text>
                    )}
                  {'constructorReference' in tyre &&
                    tyre.constructorReference && (
                      <Text
                        heading='xxs'
                        weight='semi-bold'
                        color='secondary-gray'
                        id={`product-info-constructor-reference-${tyre.ean}`}
                      >
                        {t('app.common.product.constructor_reference_value', {
                          value: tyre.constructorReference,
                        })}
                      </Text>
                    )}
                  {'cai' in tyre && tyre.cai && (
                    <Text
                      heading='xxs'
                      weight='semi-bold'
                      color='secondary-gray'
                      id={`product-info-cai-${tyre.ean}`}
                    >
                      {t('app.common.product.cai_value', {
                        value: tyre.cai,
                      })}
                    </Text>
                  )}
                </>
              )}
            </Flex>
          )}
        </div>
      </Flex>
    </div>
  );
};

export default TyreInfoBox;
